import { useState } from 'react';
import { Auth } from 'aws-amplify';
import { useHistory } from 'react-router';
import { NEW_EMAIL_REQUIRED, EXCESSIVE_ATTEMPTS } from '@iam/common';
import messageSession from 'utils/messageSession';
import { ERROR_TYPES } from 'utils/constants';
import { useStore } from 'store';
import routes from 'utils/routes';
import type { HandlerHook } from './handlerHookType';

type HandlerProps = {
  username: string;
  password: string;
};

const {
  LOGIN_PAGE,
  NOT_AUTHORIZED,
  USER_NOT_FOUND,
  INVALID_EMAIL_FORMAT,
  MISSING_EMAIL,
} = ERROR_TYPES;

const { passwordExpired, emailUpdate } = routes;

const useLoginHandler = (): HandlerHook<HandlerProps> => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    appSettings: {
      state: { portalCode },
    },
    alert: {
      actions: { setError },
    },
    user: {
      actions: { setCognitoUser, setUserCreds },
    },
  } = useStore();
  const history = useHistory();

  const handler = async ({ username, password }: HandlerProps) => {
    setError();
    setIsLoading(true);

    if (username.length < 1 || password.length < 1) {
      setIsLoading(false);
      setError(LOGIN_PAGE.EMPTY_INPUT);
      return;
    }
    try {
      const user = await Auth.signIn(username, password, { portalCode });
      setCognitoUser(user);
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        history.push(passwordExpired);
      } else {
        messageSession();
      }
    } catch (e: any) {
      const { code, message } = e;
      const errorList = {
        [NOT_AUTHORIZED]: () => {
          if (message === 'Password attempts exceeded') {
            setError(LOGIN_PAGE.EXCESSIVE_ATTEMPTS);
          } else {
            setError(LOGIN_PAGE.USER_NOT_FOUND);
          }
        },
        [USER_NOT_FOUND]: () => {
          const isInvalidEmail = message === INVALID_EMAIL_FORMAT || message === MISSING_EMAIL;
          // errors from migration aren't passed back directly and are actually a substring in the message
          if (message.indexOf(NEW_EMAIL_REQUIRED) > -1 || isInvalidEmail) {
            setUserCreds(username, password);
            history.push(emailUpdate, { isInvalidEmail });
          } else if (message.indexOf(EXCESSIVE_ATTEMPTS) > -1) {
            setError(LOGIN_PAGE.EXCESSIVE_ATTEMPTS);
          } else {
            setError(LOGIN_PAGE.USER_NOT_FOUND);
          }
        },
      };

      if (typeof errorList[code] === 'function') {
        errorList[code]();
      } else {
        setError(LOGIN_PAGE.TRY_AGAIN);
      }
    }

    setIsLoading(false);
  };

  return [isLoading, handler];
};

export default useLoginHandler;
